import React, { useState, useEffect } from "react";
import { db, auth } from "../firebaseConfig";
import { collection, getDocs, getDoc, doc, setDoc, updateDoc } from "firebase/firestore";
import {
  Button,
  Card,
  Col,
  Row,
  Form,
  Table,
  Modal,
  Alert,
} from "react-bootstrap";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";

export default function UserManagement() {
  const [role, setRole] = useState("");
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [newRoleName, setNewRoleName] = useState("");
  const [rolePermissions, setRolePermissions] = useState({
    canAccessPayrolls: false,
    canManagePayrolls: false,
    canClockInOut: false,
    canViewClockInOut: false,
    canManageLeaves: false,
    canApproveDenyLeaves: false,
    canUploadDocuments: false,
    canViewYearlyDocuments: false,
    canViewInvoice: false,
    canMakeAnnouncements: false,
    canCreateInvoices: false,
    canViewPortalArise: false,
    canSendSignRequests: false, // New permission added
  });
  const [customRoles, setCustomRoles] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editRole, setEditRole] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const fetchUserRole = async () => {
    const user = auth.currentUser;
    if (user) {
      const userDocRef = doc(db, "Users", user.uid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        setRole(userDoc.data().role || "");
      }
    }
  };

  const fetchUsers = async () => {
    try {
      const usersSnapshot = await getDocs(collection(db, "Users"));
      const userList = usersSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUsers(userList);
      setFilteredUsers(userList);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  const fetchCustomRoles = async () => {
    try {
      const rolesSnapshot = await getDocs(collection(db, "Roles"));
      const rolesList = rolesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCustomRoles(rolesList);
    } catch (err) {
      console.error("Error fetching roles:", err);
    }
  };

  useEffect(() => {
    fetchUserRole();
    fetchUsers();
    fetchCustomRoles();
  }, []);

  const handleSearch = (query) => {
    setSearchQuery(query);
    const lowerCaseQuery = query.toLowerCase();
    const filtered = users.filter(
      (user) =>
        user.fullName?.toLowerCase().includes(lowerCaseQuery) ||
        user.email?.toLowerCase().includes(lowerCaseQuery)
    );
    setFilteredUsers(filtered);
  };

  const handleCreateRole = async () => {
    if (!newRoleName) {
      alert("Role name cannot be empty.");
      return;
    }
    try {
      const newRole = {
        name: newRoleName,
        permissions: rolePermissions,
      };
      await setDoc(doc(db, "Roles", newRoleName), newRole);
      alert("Role created successfully!");
      fetchCustomRoles();
      setNewRoleName("");
      setRolePermissions({
        canAccessPayrolls: false,
        canManagePayrolls: false,
        canClockInOut: false,
        canViewClockInOut: false,
        canManageLeaves: false,
        canApproveDenyLeaves: false,
        canUploadDocuments: false,
        canViewYearlyDocuments: false,
        canViewInvoice: false,
        canMakeAnnouncements: false,
        canCreateInvoices: false,
        canViewPortalArise: false,
        canSendSignRequests: false, // Reset new permission
      });
    } catch (err) {
      console.error("Error creating role:", err);
      alert("Failed to create role.");
    }
  };

  const handleAssignRole = async () => {
    if (!selectedUser || !selectedUser.id) {
      alert("Please select a user.");
      return;
    }

    if (selectedUser.newRole === "admin" && role !== "admin") {
      alert("Only admin users can assign the 'admin' role.");
      return;
    }

    try {
      await updateDoc(doc(db, "Users", selectedUser.id), {
        role: selectedUser.newRole,
      });
      alert("Role assigned successfully!");
      fetchUsers();
      setShowModal(false);
    } catch (err) {
      console.error("Error assigning role:", err);
      alert("Failed to assign role.");
    }
  };

  const handleEditRole = (role) => {
    setEditRole(role);
    setShowEditModal(true);
  };

  const handleUpdateRolePermissions = async () => {
    if (!editRole) return;

    const updatedPermissions = {};
    Object.keys(rolePermissions).forEach((key) => {
      updatedPermissions[key] = !!editRole.permissions[key];
    });

    try {
      await updateDoc(doc(db, "Roles", editRole.name), { permissions: updatedPermissions });
      alert("Role updated successfully!");
      fetchCustomRoles();
      setShowEditModal(false);
    } catch (err) {
      console.error("Error updating role permissions:", err);
      alert("Failed to update role.");
    }
  };

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        {role !== "admin" ? (
          <Alert variant="danger">
            Access Denied: This page is accessible by admin users only.
          </Alert>
        ) : (
          <>
            <h4 className="main-title mb-4">User Management</h4>

            <Card className="mb-4 shadow-sm">
              <Card.Body>
                <h5 className="mb-3">User List</h5>
                <Form.Control
                  type="text"
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="mb-3"
                />
                <div className="table-responsive">
                  <Table striped>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((user) => (
                        <tr key={user.id}>
                          <td>{user.fullName}</td>
                          <td>{user.email}</td>
                          <td>{user.role || "No role assigned"}</td>
                          <td>
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => {
                                setSelectedUser({ ...user, newRole: "" });
                                setShowModal(true);
                              }}
                            >
                              Assign Role
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>

            <Card className="mb-4 shadow-sm">
              <Card.Body>
                <h5 className="mb-3">Create a New Role</h5>
                <Form.Group className="mb-3">
                  <Form.Label>Role Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter role name"
                    value={newRoleName}
                    onChange={(e) => setNewRoleName(e.target.value)}
                  />
                </Form.Group>
                <h6>Permissions</h6>
                {Object.keys(rolePermissions).map((key) => (
                  <Form.Check
                    key={key}
                    type="checkbox"
                    label={key.replace(/([A-Z])/g, " $1").trim()}
                    checked={rolePermissions[key]}
                    onChange={(e) =>
                      setRolePermissions({
                        ...rolePermissions,
                        [key]: e.target.checked,
                      })
                    }
                  />
                ))}
                <Button variant="success" className="mt-3" onClick={handleCreateRole}>
                  Create Role
                </Button>
              </Card.Body>
            </Card>

            <Card className="mb-4 shadow-sm">
              <Card.Body>
                <h5 className="mb-3">Edit Existing Roles</h5>
                <div className="table-responsive">
                  <Table striped>
                    <thead>
                      <tr>
                        <th>Role Name</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customRoles.map((role) => (
                        <tr key={role.id}>
                          <td>{role.name}</td>
                          <td>
                            <Button
                              variant="warning"
                              size="sm"
                              onClick={() => handleEditRole(role)}
                            >
                              Edit Permissions
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>

            <Modal show={showModal} onHide={() => setShowModal(false)}>
              <Modal.Header closeButton>
                <Modal.Title>Assign Role</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <h6>
                  Assign a role to <strong>{selectedUser?.fullName}</strong>
                </h6>
                <Form.Select
                  value={selectedUser?.newRole || ""}
                  onChange={(e) =>
                    setSelectedUser({ ...selectedUser, newRole: e.target.value })
                  }
                >
                  <option value="">Select a role</option>
                  <option value="admin">Admin</option>
                  {customRoles.map((role) => (
                    <option key={role.id} value={role.name}>
                      {role.name}
                    </option>
                  ))}
                </Form.Select>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleAssignRole}>
                  Assign Role
                </Button>
              </Modal.Footer>
            </Modal>

            <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
              <Modal.Header closeButton>
                <Modal.Title>Edit Role Permissions</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {editRole && (
                  <>
                    <h6>Role: {editRole.name}</h6>
                    <h6>Permissions</h6>
                    {Object.keys(rolePermissions).map((key) => (
                      <Form.Check
                        key={key}
                        type="checkbox"
                        label={key.replace(/([A-Z])/g, " $1").trim()}
                        checked={editRole.permissions[key] || false}
                        onChange={(e) =>
                          setEditRole({
                            ...editRole,
                            permissions: {
                              ...editRole.permissions,
                              [key]: e.target.checked,
                            },
                          })
                        }
                      />
                    ))}
                  </>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleUpdateRolePermissions}>
                  Update Permissions
                </Button>
              </Modal.Footer>
            </Modal>
          </>
        )}
        <Footer />
      </div>
    </React.Fragment>
  );
}
